import { Link, useLocation } from '@tanstack/react-router'
import { Search, UserCog } from 'lucide-react'
import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import { BarraDeBusca, NOME_DA_BUSCA } from '@/components/busca/BarraDeBusca'
import { AcoesDoGestor } from '@/components/gestor/AcoesDoGestor'
import { Button, buttonVariants } from '@/components/ui/button'

const SUBTITULO = 'Valor de Conservação em Áreas Privadas'

/*
 * Cabeçalho do observatório, como o protótipo do PO o desenha: uma faixa azul
 * de altura fixa, numa linha só, com a marca à esquerda, a busca no meio e, à
 * direita, a faixa reservada aos links. Baixo porque a tela é de mapa: cada
 * pixel do cabeçalho sai da altura do mapa.
 *
 * A marca é o selo do Projeto GEF Áreas Privadas, o título com o "gef" em verde
 * e o subtítulo. O verde do título é mais claro que o desenhado: o do protótipo
 * fica abaixo de 3:1 sobre o azul (`--gef-marca-destaque`).
 *
 * A busca é um campo só (`BarraDeBusca`). Abaixo de 1280px o campo fica atrás
 * de um atalho, que o abre sobre o cabeçalho, na largura da tela; escolher uma
 * sugestão, ou a seta de voltar, fecha o campo e devolve o foco ao atalho.
 * Link para página que ainda não existe ("Sobre o Índice") não entra. O botão
 * "Área do Gestor" fica na faixa da direita; abaixo de 1280px, onde a faixa
 * some, vira só o ícone, com o mesmo nome acessível.
 *
 * Dentro da Área do Gestor (`/gestor/*`) o cabeçalho é o mesmo, mas a busca do
 * mapa sai e entram o caminho de volta ao observatório e o chip da pessoa.
 * Abaixo de 576px a marca fica só com o selo: o texto dela não cabe ao lado do
 * botão e do chip.
 *
 * Sobre o azul, o foco visível troca para o branco (`--gef-foco`): o azul dos
 * links sumiria no fundo.
 */
export function AppHeader() {
  const naAreaDoGestor = useLocation({
    select: (local) => local.pathname === '/gestor' || local.pathname.startsWith('/gestor/'),
  })
  const [buscaAberta, setBuscaAberta] = useState(false)
  const atalho = useRef<HTMLButtonElement>(null)

  function fecharBusca() {
    setBuscaAberta(false)
    atalho.current?.focus()
  }

  return (
    <header
      className={
        'relative flex h-(--gef-cabecalho-altura) flex-none items-center gap-(--gef-cabecalho-espaco) px-(--gef-cabecalho-margem) ' +
        'bg-(--gef-cabecalho-fundo) text-(--gef-cabecalho-texto) [--gef-foco:var(--gef-texto-inverso)]'
      }
    >
      {/* No desktop a marca tem a largura do protótipo (`--gef-marca-largura`)
          ou a que o texto dela pede, se for maior — ela não pode perder o
          subtítulo em notebook —, e a busca fica com o resto da linha. Abaixo
          de 1280px a busca sai da linha e a marca encolhe com ela. */}
      <div
        data-slot="marca"
        className="flex min-w-0 flex-1 items-center gap-(--gef-marca-espaco) min-[1280px]:min-w-(--gef-marca-largura) min-[1280px]:flex-none"
      >
        <img
          src="/selo-gef.png"
          alt="GEF Áreas Privadas"
          className="h-(--gef-marca-selo-altura) w-(--gef-marca-selo-largura) flex-none"
        />
        {/* Título e subtítulo nos tamanhos do protótipo. Os dois cortam o que
            vaza (`truncate`), inclusive para cima: numa linha mais baixa que a
            letra, o acento de maiúscula (o "Á" de "Áreas") some. Por isso a
            entrelinha é a menor em que a letra cabe. O "gef" é escrito em
            maiúsculas e só aparece em minúsculas: o leitor de tela lê a sigla. */}
        <div className={cn('flex min-w-0 flex-col', naAreaDoGestor && 'max-sm:hidden')}>
          <p className="m-0 truncate text-(length:--gef-marca-titulo-tamanho) leading-(--gef-marca-titulo-entrelinha) font-bold">
            Observatório <span className="text-(--gef-marca-destaque) lowercase">GEF</span>
          </p>
          <p className="m-0 truncate text-(length:--gef-marca-subtitulo-tamanho) leading-normal font-medium">
            {SUBTITULO}
          </p>
        </div>
      </div>

      {naAreaDoGestor ? (
        <>
          <div className="flex-1" />
          <AcoesDoGestor />
        </>
      ) : (
        <>
          {/* A busca vem logo depois da marca e cresce com a janela, como no
            protótipo: ocupa a linha até a faixa dos links, reservada logo
            abaixo. Abaixo de 1280px o campo some da linha e abre sobre o
            cabeçalho inteiro, pelo atalho. */}
          <BarraDeBusca aberta={buscaAberta} aoFechar={fecharBusca} aoAplicar={fecharBusca} />

          {/* A faixa dos links à direita, com a largura que eles ocupam no
            protótipo ("Sobre o Índice" e "Área do Gestor", e o espaço entre
            eles). "Sobre o Índice" ainda não existe, mas a faixa mantém a
            largura toda: sem ela a busca ficaria mais larga que no protótipo e
            encolheria quando o segundo link chegasse. Abaixo de 1280px a faixa
            encolhe ao ícone da Área do Gestor. */}
          <div className="flex flex-none justify-end min-[1280px]:w-(--gef-cabecalho-acoes-largura)">
            <Link
              to="/gestor"
              className={cn(
                buttonVariants({ variant: 'primary', size: 'icon' }),
                'min-[1280px]:w-auto min-[1280px]:px-4'
              )}
            >
              <UserCog aria-hidden="true" className="min-[1280px]:hidden" />
              <span className="sr-only min-[1280px]:not-sr-only">Área do Gestor</span>
            </Link>
          </div>

          <Button
            ref={atalho}
            variant="ghost"
            size="icon"
            aria-label={NOME_DA_BUSCA}
            aria-expanded={buscaAberta}
            onClick={() => setBuscaAberta(true)}
            className="flex-none min-[1280px]:hidden"
          >
            <Search aria-hidden="true" />
          </Button>
        </>
      )}
    </header>
  )
}
